import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, CreditCard, LogOut, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Email update state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Billing state
  const [loadingPortal, setLoadingPortal] = useState(false);

  const getSessionToken = () => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') return value;
    }
    return localStorage.getItem('session_token');
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = getSessionToken();
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !emailPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setUpdatingEmail(true);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/auth/update-email`, 
        { new_email: newEmail, password: emailPassword },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success('Email updated successfully');
      setUser({ ...user, email: newEmail });
      setNewEmail('');
      setEmailPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/billing/portal`, 
        { return_url: window.location.origin + '/account' },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
      setLoadingPortal(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = getSessionToken();
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('session_token');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar user={user} onLogout={handleLogout} />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account and billing</p>
        </div>

        {/* Profile Section */}
        <Card className="mb-6" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile
            </CardTitle>
            <CardDescription>Your account information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Current Email</Label>
              <Input value={user?.email || ''} disabled className="bg-muted" />
            </div>
            <div>
              <Label>Plan</Label>
              <Input value={user?.plan_display_name || user?.plan || 'Free'} disabled className="bg-muted" />
            </div>
            
            <Separator className="my-4" />
            
            <form onSubmit={handleUpdateEmail} className="space-y-4">
              <h4 className="font-medium">Update Email</h4>
              <div>
                <Label htmlFor="newEmail">New Email</Label>
                <Input 
                  id="newEmail" 
                  type="email" 
                  value={newEmail} 
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  data-testid="new-email-input"
                />
              </div>
              <div>
                <Label htmlFor="emailPassword">Confirm Password</Label>
                <Input 
                  id="emailPassword" 
                  type="password" 
                  value={emailPassword} 
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="email-password-input"
                />
              </div>
              <Button type="submit" disabled={updatingEmail} data-testid="update-email-btn">
                {updatingEmail ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                Update Email
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card className="mb-6" data-testid="password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Password
            </CardTitle>
            <CardDescription>Change your password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input 
                  id="currentPassword" 
                  type="password" 
                  value={currentPassword} 
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input 
                  id="newPassword" 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input 
                  id="confirmPassword" 
                  type="password" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                {changingPassword ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                Change Password
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Billing Section */}
        <Card className="mb-6" data-testid="billing-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Billing
            </CardTitle>
            <CardDescription>Manage your subscription and payment methods</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Subscription Status</p>
                <p className="text-sm text-muted-foreground">
                  {user?.subscription_status === 'active' ? 'Active' : 
                   user?.subscription_status === 'past_due' ? 'Past Due' : 
                   'No active subscription'}
                </p>
              </div>
              {user?.subscription_status === 'active' && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Active</span>
              )}
            </div>
            
            {user?.stripe_customer_id ? (
              <Button onClick={handleManageBilling} disabled={loadingPortal} className="w-full" data-testid="manage-billing-btn">
                {loadingPortal ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Manage Billing
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">No billing account. Subscribe to a plan to manage billing.</p>
            )}
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card data-testid="security-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>Account security options</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={handleLogout} className="w-full" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default Account;
